import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Spinner from '../../../../components/LazyWidgets/Spinner';
import Menu from '../../../../components/LazyWidgets/Menu';
import ReportError from '../ReportError';
import {
  loadStudentUnitReport as loadStudentUnitReportAction,
  sendAnalyticsEvent,
  updateStudentUnitReport as updateStudentUnitReportAction,
} from '../../../actions/reports';
import LearningObjectives from '../../../../components/LearningObjectives/LearningObjectives';
import ReportBreadcrumbs from '../../../../components/ReportBreadcrumbs/ReportBreadcrumbs';
import EmailWithTooltip from '../../../components/EmailTooltip/EmailWithTooltip';
import s from './quantQualStudentUnit.module.css';
import style from '../QuantQualModule/QuantQualModule.module.css';
import StudentPerformanceByAssignmentComponent from './studentPerformanceByAssignmentComponent/studentPerformanceByAssignmentComponent';
import StudentHeroCard from './StudentHeroCard/StudentHeroCard';
import { NULL_STATE_TYPE } from '../../../actions/constants';
import PerformanceBySubUnitCard from '../QuantQualModule/qqComponents/performanceBySubmodulesComponent';
import ExternalFrameworkCard from '../../../../components/ExternalFrameworkCard';
import { getConfigObject } from '../../../util/config';

class QQStudentModuleReport extends React.Component {
  constructor(props) {
    super(props);
    this.handleUnitUpdate = this.handleUnitUpdate.bind(this);
    this.containerRef = React.createRef();
    this.adjustDropDowns = this.adjustDropDowns.bind(this);
    this.getLearningObjectiveData = this.getLearningObjectiveData.bind(this);
  }

  componentDidMount() {
    const {
      courseId,
      loadStudentUnitReport,
      unitId,
      studentId,
    } = this.props;
    loadStudentUnitReport(courseId, unitId, studentId);
  }

  componentDidUpdate() {
    this.adjustDropDowns();
  }

  getBeautifyData(data) {
    return data.map(row => ({
      ...row,
      correctOnFirstTrySortValue: row.correctOnFirstTry,
      isNullState: !(row.correctOnFirstTry >= 0),
      correctOnFirstTry: row.correctOnFirstTry >= 0 ? `${Math.round(row.correctOnFirstTry * 100)}%` : '—',
      children: row.children.map(element => ({
        ...element,
        correctOnFirstTrySortValue: element.correctOnFirstTry,
        isNullState: !(element.correctOnFirstTry >= 0),
        correctOnFirstTry: element.correctOnFirstTry >= 0 ? `${Math.round(element.correctOnFirstTry * 100)}%` : '—',
        completedQuestions: element.completedQuestions >= 0 ? element.completedQuestions : '—',
      })),
    }));
  }

  getLearningObjectiveData(data, unitNotStarted, unitAssignments, reportType) {
    const learningObjectiveData = data && data.length !== 0 ? this.getBeautifyData(data) : [];
    const lOHeader = [
      {
        accessor: 'name',
        Header: 'Objective Title',
      },
      {
        accessor: 'startedQuestions',
        Header: 'Questions Started',
      },
      {
        accessor: 'completedQuestions',
        Header: 'Questions completed',
      },
      {
        accessor: 'correctOnFirstTry',
        sortValue: 'correctOnFirstTrySortValue',
        Header: 'Questions Correct On First Try',
      },
    ];

    const cellStyling = [
      {
        accessor: 'startedQuestions',
        alignment: 'right',
      },
      {
        accessor: 'completedQuestions',
        alignment: 'right',
      },
      {
        accessor: 'correctOnFirstTry',
        alignment: 'right',
      },
    ];

    const lOModal = {
      title: (<p>Student Performance by Learning Objective</p>),
      content: (
        <div>
          <p>
            <b>Questions started</b> represents the number of questions assessing this learning objective that this student began.<br />
          </p>
          <p>
            <b>Questions completed</b> represents the number of questions assessing this learning objective that your students completed.<br />
          </p>
          <p>
            <b>Questions correct on first try</b> represents, across all attempted questions assessing this learning objective, the proportion that were correct on this student’s first try.<br />
          </p>
        </div>
      ),
    };

    const subtitleTextFunc = (row, amount) => (
      <div>
        This student has scored below 60% on
        <b>{` ${row.lowScoring} of ${amount} `}</b>
        {`Learning Objective${amount > 1 ? 's' : ''}`}
      </div >
    );

    const nullStateMsg = this.getNullState(data, unitNotStarted, unitAssignments);

    return (
      <LearningObjectives
        data={data ? learningObjectiveData : []}
        onTabChange={this.props.onTabChange}
        accessors={lOHeader}
        modal={lOModal}
        nullStateMessage={nullStateMsg}
        getSubtitleLOText={subtitleTextFunc}
        primaryMeasureAccessor="correctOnFirstTry"
        cellStyling={cellStyling}
        className={s.studentLO}
        reportType={reportType}
      />);
  }

  getNullState(data, unitNotStarted, unitAssignments) {
    let nullStateType = null;
    if (unitAssignments === 0) {
      nullStateType = NULL_STATE_TYPE.noAssignments;
    } else if (unitNotStarted) {
      nullStateType = NULL_STATE_TYPE.noActivity;
    } else if (!data || !data.length) {
      nullStateType = NULL_STATE_TYPE.noQuestionsTagged;
    }

    if (!nullStateType) return null;

    return (
      <div className={s.qqLOTitleContainerNoText}>
        <p className={s.qqLONoDataText}>
          {{
            [NULL_STATE_TYPE.noAssignments]: <span>There are no assignments with Learning Objectives in this Unit.<br />
              Go to the <button role="link" className={s.qqLONoDataLink} onClick={() => { this.props.onTabChange('assignments'); }}>Course Planner</button> tab to setup and manage assignments.</span>,
            [NULL_STATE_TYPE.noActivity]: <span>After this student starts working on the assignments in this Unit, check here to see how they are performing against Learning Objectives.</span>,
            [NULL_STATE_TYPE.noQuestionsTagged]: <span>The questions started by this student do not cover any learning objectives. After this student starts working on learning objectives in this unit,
              check here to see the student&#39;s performance on those objectives.</span>,
          }[nullStateType]}
        </p>
      </div>
    );
  }

  handleUnitUpdate(value) {
    const { units, updateStudentUnitReport, studentId } = this.props;
    const newActiveUnit = units[value];
    updateStudentUnitReport(newActiveUnit.id, studentId);
    this.props.onTabChange(`reports/type/student-unit/${studentId}/${newActiveUnit.id}`);
  }

  calculateFakeWidth(text) {
    const tempDiv = document.createElement('div');
    document.body.appendChild(tempDiv);
    tempDiv.style.fontSize = '17px';
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = -1000;
    tempDiv.style.top = -1000;
    tempDiv.style['white-space'] = 'nowrap';
    tempDiv.style.lineHeight = '32px';
    tempDiv.innerHTML = text;
    const width = tempDiv.clientWidth;
    document.body.removeChild(tempDiv);
    return width;
  }

  adjustDropDowns() {
    if (this.containerRef.current) {
      const dropDowns = this.containerRef.current.getElementsByTagName('select');
      for (const dropDown of dropDowns) {
        const fakeWidth = this.calculateFakeWidth(dropDown.options[dropDown.selectedIndex].textContent);
        if (fakeWidth > dropDown.offsetWidth + 3) {
          dropDown.parentElement.classList.add(s.ellipsis);
        } else {
          dropDown.parentElement.classList.remove(s.ellipsis);
        }
      }
    }
  }

  render() {
    if (this.props.loading) return <Spinner className={style.qqReportSpinner} />;

    const {
      error,
      studentUnitReport: {
        error: errorReport,
      },
    } = this.props;

    if (error || errorReport) return <ReportError />;

    const {
      units,
      activeUnit,
      studentUnitReport: {
        student,
        performanceBySubunit,
        performanceByAssignment,
        performanceByLearningObjective,
        performanceByExternalFrameworks,
        unitHighlights,
      },
      courseId,
      onTabChange,
      reportType
    } = this.props;

    const breadcrumbItems = [
      { text: 'Overview', targetUrl: 'reports' },
      { text: activeUnit ? activeUnit.name : 'Unit Report', targetUrl: activeUnit ? `reports/type/unit/${activeUnit.id}` : undefined },
      { text: student ? student.name : 'Student Report' }];

    const reportTitle = () => (
      <div>
        <div className={s.studentTitle}>
          {student.name || ''}
        </div>
        <EmailWithTooltip
          email={student.email || ''}
          className={s.studentTitleEmail}
        />
      </div>
    );

    return (
      <div
        className={s.reportContainer}
        id="student-container"
        ref={this.containerRef}
      >
        {reportTitle()}
        <ReportBreadcrumbs items={breadcrumbItems} onTabChange={onTabChange} />
        <Menu
          className={style.qqModuleMenu}
          id="unitId1"
          options={units}
          onChange={this.handleUnitUpdate}
          selectedLabel={activeUnit.name}
          hasLabel
          label="Unit:"
          boxed
        />
        <StudentHeroCard
          unitHighlights={unitHighlights}
          onTabChange={this.props.onTabChange}
          heroType="Unit"
        />
        <PerformanceBySubUnitCard
          data={performanceBySubunit}
          onTabChange={this.props.onTabChange}
          studentId={student.id}
          reportType={reportType}
          sendEvent={sendAnalyticsEvent}
        />
        {performanceByAssignment && (
          <StudentPerformanceByAssignmentComponent
            data={performanceByAssignment}
            courseId={courseId}
            onTabChange={onTabChange}
            reportType={reportType}
            sendEvent={sendAnalyticsEvent}
          />
        )}

        {getConfigObject().FEATURE_USE_EXTERNAL_INSIGHTS && (
          <ExternalFrameworkCard
            rows={performanceByExternalFrameworks}
            reportType={reportType}
          />
        )}

        {this.getLearningObjectiveData(
          performanceByLearningObjective,
          unitHighlights.notStarted,
          unitHighlights.assignments,
          reportType
        )}
      </div>
    );
  }
}

QQStudentModuleReport.propTypes = {
  reportType: PropTypes.string,
  studentId: PropTypes.string,
  unitId: PropTypes.string,
  courseId: PropTypes.string,
  onTabChange: PropTypes.func,
  loadStudentUnitReport: PropTypes.func,
  updateStudentUnitReport: PropTypes.func,
  units: PropTypes.array,
  activeUnit: PropTypes.object,
  studentUnitReport: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.any,
};

function mapStateToProps(state) {
  return {
    courseId: state.reports.courseAbstract.id,
    units: state.reports.unitList.units,
    studentUnitReport: state.reports.studentUnitReport,
    activeUnit: state.reports.unitList.studentActiveUnit,
    loading: !state.reports.unitList.units || state.reports.unitList.loading ||
      !state.reports.studentUnitReport || state.reports.studentUnitReport.loading,
    error: state.reports.unitList.error,
  };
}

const mapDispatchToProps = dispatch => bindActionCreators({
  loadStudentUnitReport: loadStudentUnitReportAction,
  updateStudentUnitReport: updateStudentUnitReportAction,
  sendAnalyticsEvent,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(QQStudentModuleReport);
